import { useQueries, useQuery } from '@tanstack/react-query';
import _ from 'lodash';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { DataItem } from '../../@types/DataItem';
import { GerenciadorPrecos } from '../../@types/GerenciadorPrecos';
import { queryClient } from '../../App';
import { useDebounce } from '../../hooks';
import { selectorFiltersIsVisible } from '../../reducers/gerenciadorPrecos';
import { RESET_FILTERS_CACHE_BY_KEY, SET_FILTER_CACHE_BY_KEY, selectorFiltersCache } from '../../reducers/gerenciadorPrecos/filters/filtersCache';
import { SET_FILTER_DATA_BY_KEY, selectorFiltersData } from '../../reducers/gerenciadorPrecos/filters/filtersData';
import { RESET_FILTERS_SEARCH_BY_KEY, SET_FILTERS_SEARCH_BY_KEY, selectorFiltersSearch } from '../../reducers/gerenciadorPrecos/filters/filtersSearch';
import { RESET_FILTERS_VALUE_BY_KEY, SET_FILTERS_VALUE_BY_KEY, selectorFiltersValues } from '../../reducers/gerenciadorPrecos/filters/filtersValues';
import { selectorSegmentos } from '../../reducers/gerenciadorPrecos/segmentos';
import { useCompetitorFiltersStore } from '../../store/useCompetitorFiltersStore';
import { checkIfIsLoading } from '../../utils';
import {
  getPricerAssignmentsForFilter,
  getPricingScopeLevel,
  ResponsibleFilterOption,
  toResponsibleOption,
} from '../../pages/Administrador/Produtos/Components/SetupIpaPricingResponsibilities/service';
import { pricingScopeKeys } from '../../pages/Administrador/Produtos/Components/SetupIpaPricingResponsibilities/hooks/usePricingScopeQueries';
import { PricerAssignment } from '../../pages/Administrador/Produtos/Components/SetupIpaPricingResponsibilities/types';
import {
  SegmentData,
  StoreTypeFilter,
  listClusters,
  listFamilies,
  listPriceStatus,
  listProductBrands,
  listSegment,
  listStoreType,
  listStores,
  listSuppliers,
  listaSegmentacao,
} from '../FilterSection/services';
import { getCascadingFilterParams, getCascadingFilterParamsSignature, hasCascadingFilterParams, isCascadingFilterKey } from './data/cascadingFilters';
import { resetPriceManagerFilters } from './data/utils';
import { applyResponsibleCategories, clearResponsibleCategories, syncInjectedCategoriesAfterManualChange } from './data/responsibleFilter';

export type Keys = GerenciadorPrecos.FilterKeys;
const getInitialCategoryDataCacheKey = (key: Keys) => ['ipa.filter.segmentos.initial', key] as const;
const isValidPricingScopeLevel = (level?: number | null): level is number => typeof level === 'number' && Number.isInteger(level) && level >= 1 && level <= 7;

const useNewFilterBox = () => {
  const dispatch = useDispatch();
  const debounce = useDebounce(500);
  const data = useSelector(selectorFiltersData);
  const cache = useSelector(selectorFiltersCache);
  const values = useSelector(selectorFiltersValues);
  const search = useSelector(selectorFiltersSearch);
  const cattegories = useSelector(selectorSegmentos);
  const isVisible = useSelector(selectorFiltersIsVisible);
  const clearCompetitorFilters = useCompetitorFiltersStore(state => state.clearCompetitorFilters);

  const [hasPastedValues, setHasPastedValues] = useState(false);
  const productFamilyCascadingParams = useMemo(() => getCascadingFilterParams(values, 'productFamilyIds'), [values]);
  const initialCategoryDataRef = useRef<Partial<Record<Keys, DataItem[]>>>({});
  const lastLoadedCategoryCascadingSignatureRef = useRef<Partial<Record<Keys, string>>>({});
  const responsibleAssignmentsRef = useRef(new Map<string, PricerAssignment>());
  const responsibleIdRef = useRef<string | null>(null);
  const responsibleInjectedCategoriesRef = useRef<string[]>([]);
  const responsibleCategoryLevelRef = useRef<Keys | null>(null);

  const getInitialCategoryData = useCallback((key: Keys) => {
    const cachedData = initialCategoryDataRef.current[key] ?? queryClient.getQueryData<DataItem[]>(getInitialCategoryDataCacheKey(key));

    if (cachedData) {
      initialCategoryDataRef.current[key] = cachedData;
    }

    return cachedData;
  }, []);

  const onSuccess = useCallback(
    (key: Keys, data: Array<DataItem | SegmentData | StoreTypeFilter>) => {
      const newData = key === 'storeType' ? data : _.uniqBy([...(cache[key] ?? []), ...data], 'value');
      dispatch(SET_FILTER_DATA_BY_KEY({ key, data: newData }));

      if (key === 'productIds' && hasPastedValues) {
        const value = newData.map(({ value }) => value) as string[];
        dispatch(
          SET_FILTER_CACHE_BY_KEY({
            key,
            cache: newData as DataItem[],
          }),
        );
        dispatch(SET_FILTERS_VALUE_BY_KEY({ key, value }));
        setHasPastedValues(false);
      }
    },
    [dispatch, cache, hasPastedValues],
  );

  const responsibleSearch = search.responsible ?? '';
  const responsibleAssignmentsParams = useMemo(() => ({ search: responsibleSearch, page: 0, size: 20, sort: 'userEmail,asc' }), [responsibleSearch]);

  const { data: pricingScopeLevel, fetchStatus: loadingPricingScopeLevel } = useQuery({
    queryKey: pricingScopeKeys.level(),
    queryFn: getPricingScopeLevel,
    enabled: isVisible,
  });
  const hasValidPricingScopeLevel = isValidPricingScopeLevel(pricingScopeLevel?.level);

  const { data: responsibleAssignments, fetchStatus: loadingResponsibleAssignments } = useQuery({
    queryKey: pricingScopeKeys.assignments(responsibleAssignmentsParams),
    queryFn: () => getPricerAssignmentsForFilter(responsibleSearch),
    enabled: isVisible && hasValidPricingScopeLevel,
    keepPreviousData: true,
  });

  useEffect(() => {
    responsibleAssignments?.forEach(assignment => {
      responsibleAssignmentsRef.current.set(assignment.id, assignment);
    });
  }, [responsibleAssignments]);

  useEffect(() => {
    const responsibleOptions = hasValidPricingScopeLevel
      ? _.uniqBy([...(cache.responsible ?? []), ...(responsibleAssignments ?? []).map(toResponsibleOption)], 'value')
      : [];

    dispatch(SET_FILTER_DATA_BY_KEY({ key: 'responsible', data: responsibleOptions }));
  }, [cache.responsible, dispatch, hasValidPricingScopeLevel, responsibleAssignments]);

  useEffect(() => {
    const responsibleId = values.responsible?.[0];

    if (!responsibleId) {
      responsibleIdRef.current = null;
      responsibleInjectedCategoriesRef.current = [];
      responsibleCategoryLevelRef.current = null;
      return;
    }

    if (responsibleIdRef.current !== responsibleId) {
      responsibleIdRef.current = responsibleId;
      responsibleInjectedCategoriesRef.current = [];
      responsibleCategoryLevelRef.current = null;
    }

    const responsibleOption = (cache.responsible ?? []).find(item => item.value === responsibleId) as ResponsibleFilterOption | undefined;
    if (!responsibleOption?.categoryLevel) {
      const assignment = responsibleAssignmentsRef.current.get(responsibleId);
      const level = pricingScopeLevel?.level;

      if (!assignment || !isValidPricingScopeLevel(level)) return;

      const categoryLevel = `categoryLevel${level}` as Keys;
      const currentCategoryValues = (values[categoryLevel] as string[] | undefined) ?? [];
      const projection = applyResponsibleCategories(currentCategoryValues, [], assignment.categories ?? []);
      const assignedOptions = (assignment.categories ?? []).map(category => ({ label: category, value: category }));
      const categoryData = (data[categoryLevel] as DataItem[] | undefined) ?? [];
      const categoryCache = (cache[categoryLevel] as DataItem[] | undefined) ?? [];
      const mergedCategoryData = _.uniqBy([...categoryData, ...categoryCache, ...assignedOptions], 'value');
      const categoryOptionsByValue = new Map(mergedCategoryData.map(option => [option.value, option]));
      const selectedCategoryCache = projection.values.map(value => categoryOptionsByValue.get(value) ?? { label: value, value });
      const visibleCategoryData = _.uniqBy([...selectedCategoryCache, ...mergedCategoryData], 'value');

      dispatch(SET_FILTER_DATA_BY_KEY({ key: categoryLevel, data: visibleCategoryData }));
      dispatch(SET_FILTER_CACHE_BY_KEY({ key: categoryLevel, cache: selectedCategoryCache }));
      dispatch(SET_FILTERS_VALUE_BY_KEY({ key: categoryLevel, value: projection.values }));

      const hydratedOption = {
        ...(responsibleOption ?? toResponsibleOption(assignment)),
        categoryLevel,
        injectedCategories: projection.injected,
      };

      dispatch(SET_FILTER_CACHE_BY_KEY({ key: 'responsible', cache: [hydratedOption] }));
      responsibleCategoryLevelRef.current = categoryLevel;
      responsibleInjectedCategoriesRef.current = projection.injected;
      return;
    }

    responsibleCategoryLevelRef.current = responsibleOption.categoryLevel;
    responsibleInjectedCategoriesRef.current = responsibleOption.injectedCategories ?? [];
  }, [cache, data, dispatch, pricingScopeLevel, responsibleAssignments, values]);

  const { fetchStatus: loadingProductFamilyIds } = useQuery({
    queryKey: ['ipa.filter.productFamilyIds', search.productFamilyIds],
    queryFn: () => listFamilies(search.productFamilyIds, productFamilyCascadingParams),
    onSuccess: data => onSuccess('productFamilyIds', data),
  });

  const { fetchStatus: loadingStoreIds } = useQuery({
    queryKey: ['ipa.filter.storeIds', search.storeIds],
    queryFn: () => listStores(search.storeIds),
    onSuccess: data => onSuccess('storeIds', data),
  });

  const { fetchStatus: loadingClusters } = useQuery({
    queryKey: ['ipa.filter.clusters', search.clusters],
    queryFn: () => listClusters(search.clusters),
    onSuccess: data => onSuccess('clusters', data),
  });

  const { fetchStatus: loadingPriceStatus } = useQuery({
    queryKey: ['ipa.filter.priceStatus'],
    queryFn: listPriceStatus,
    onSuccess: data => onSuccess('priceStatus', data),
  });

  const { fetchStatus: loadingSegmentos } = useQuery({
    queryKey: ['ipa.filter.segmentos'],
    queryFn: listaSegmentacao,
    onSuccess: data => onSuccess('segmentos', data),
  });

  const { fetchStatus: loadingProductBrand } = useQuery({
    queryKey: ['ipa.filter.productBrand', search.productBrand],
    queryFn: () => listProductBrands(search.productBrand),
    onSuccess: data => onSuccess('productBrand', data),
  });

  const { fetchStatus: loadingSupplier } = useQuery({
    queryKey: ['ipa.filter.supplier', search.supplier],
    queryFn: () => listSuppliers(search.supplier),
    onSuccess: data => onSuccess('supplier', data),
  });

  const { fetchStatus: loadingStoreType } = useQuery({
    queryKey: ['ipa.filter.storeType', search.storeType],
    queryFn: () => listStoreType(search.storeType || ''),
    onSuccess: data => onSuccess('storeType', data),
  });

  const results = useQueries({
    queries: cattegories.map(({ value: key, level }) => {
      const cascadingParams = getCascadingFilterParams(values, key);

      return {
        queryKey: ['ipa.filter.segmentos', key, search[key]],
        queryFn: () => listSegment(search[key], level, cascadingParams),
        onSuccess: (data: DataItem[]) => {
          if (!search[key] && !hasCascadingFilterParams(cascadingParams)) {
            initialCategoryDataRef.current[key] = data;
            queryClient.setQueryData(getInitialCategoryDataCacheKey(key), data);
          }

          if (!search[key]) {
            lastLoadedCategoryCascadingSignatureRef.current[key] = getCascadingFilterParamsSignature(cascadingParams);
          }

          onSuccess(key, data);
        },
        enabled: !!cattegories.length,
      };
    }),
  });

  const onSearch = useCallback(
    (key: Keys, query: string) => {
      debounce(() => dispatch(SET_FILTERS_SEARCH_BY_KEY({ key, query })));
    },
    [dispatch, debounce],
  );

  const onChange = useCallback(
    (key: Keys, value: string[]) => {
      dispatch(SET_FILTERS_VALUE_BY_KEY({ key, value }));

      const selectedCache = data[key]?.filter(item => {
        return value.includes(item.value);
      });

      if (selectedCache) dispatch(SET_FILTER_CACHE_BY_KEY({ key, cache: selectedCache }));

      if (responsibleCategoryLevelRef.current === key) {
        responsibleInjectedCategoriesRef.current = syncInjectedCategoriesAfterManualChange(value, responsibleInjectedCategoriesRef.current);

        const responsibleId = values.responsible?.[0];
        const responsibleCache = cache.responsible ?? [];
        if (responsibleId && responsibleCache.some(item => item.value === responsibleId)) {
          dispatch(
            SET_FILTER_CACHE_BY_KEY({
              key: 'responsible',
              cache: responsibleCache.map(item =>
                item.value === responsibleId ? { ...item, injectedCategories: responsibleInjectedCategoriesRef.current } : item,
              ),
            }),
          );
        }
      }
    },
    [cache.responsible, data, dispatch, values.responsible],
  );

  const onCleanResponsible = useCallback(() => {
    const categoryLevel = responsibleCategoryLevelRef.current;
    const injectedCategories = responsibleInjectedCategoriesRef.current;

    if (categoryLevel && injectedCategories.length) {
      const nextValues = clearResponsibleCategories((values[categoryLevel] as string[] | undefined) ?? [], injectedCategories);
      dispatch(SET_FILTERS_VALUE_BY_KEY({ key: categoryLevel, value: nextValues }));
    }

    if (search.responsible?.length) {
      dispatch(RESET_FILTERS_SEARCH_BY_KEY({ key: 'responsible' }));
    }

    dispatch(RESET_FILTERS_VALUE_BY_KEY({ key: 'responsible' }));
    dispatch(RESET_FILTERS_CACHE_BY_KEY({ key: 'responsible' }));
    responsibleIdRef.current = null;
    responsibleInjectedCategoriesRef.current = [];
    responsibleCategoryLevelRef.current = null;
  }, [dispatch, search.responsible, values]);

  const onResponsibleChange = useCallback(
    (value: string | null) => {
      if (!value) {
        onCleanResponsible();
        return;
      }

      const assignment = responsibleAssignmentsRef.current.get(value);
      const level = pricingScopeLevel?.level;
      if (!assignment || !isValidPricingScopeLevel(level)) return;

      const categoryLevel = `categoryLevel${level}` as Keys;
      const previousCategoryLevel = responsibleCategoryLevelRef.current;
      const previousInjectedCategories = responsibleInjectedCategoriesRef.current;
      let currentValues = (values[categoryLevel] as string[] | undefined) ?? [];

      if (previousCategoryLevel) {
        const previousValues = clearResponsibleCategories((values[previousCategoryLevel] as string[] | undefined) ?? [], previousInjectedCategories);
        if (previousCategoryLevel === categoryLevel) {
          currentValues = previousValues;
        }
        dispatch(SET_FILTERS_VALUE_BY_KEY({ key: previousCategoryLevel, value: previousValues }));
      }

      const projection = applyResponsibleCategories(
        currentValues,
        previousCategoryLevel === categoryLevel ? previousInjectedCategories : [],
        assignment.categories ?? [],
      );

      const assignedOptions = (assignment.categories ?? []).map(category => ({ label: category, value: category }));
      const categoryData = (data[categoryLevel] as DataItem[] | undefined) ?? [];
      const categoryCache = (cache[categoryLevel] as DataItem[] | undefined) ?? [];
      const mergedCategoryData = _.uniqBy([...categoryData, ...categoryCache, ...assignedOptions], 'value');
      const categoryOptionsByValue = new Map(mergedCategoryData.map(option => [option.value, option]));
      const selectedCategoryCache = projection.values.map(value => categoryOptionsByValue.get(value) ?? { label: value, value });
      const visibleCategoryData = _.uniqBy([...selectedCategoryCache, ...mergedCategoryData], 'value');

      dispatch(SET_FILTER_DATA_BY_KEY({ key: categoryLevel, data: visibleCategoryData }));
      dispatch(SET_FILTER_CACHE_BY_KEY({ key: categoryLevel, cache: selectedCategoryCache }));
      dispatch(SET_FILTERS_VALUE_BY_KEY({ key: categoryLevel, value: projection.values }));
      dispatch(SET_FILTERS_VALUE_BY_KEY({ key: 'responsible', value: [value] }));
      dispatch(
        SET_FILTER_CACHE_BY_KEY({
          key: 'responsible',
          cache: [
            {
              ...toResponsibleOption(assignment),
              categoryLevel,
              injectedCategories: projection.injected,
            },
          ],
        }),
      );

      responsibleIdRef.current = value;
      responsibleInjectedCategoriesRef.current = projection.injected;
      responsibleCategoryLevelRef.current = categoryLevel;
    },
    [cache, data, dispatch, onCleanResponsible, pricingScopeLevel, values],
  );

  const onClean = useCallback(
    (key: Keys) => {
      const nextValues = {
        ...values,
        [key]: [],
      };

      if (responsibleCategoryLevelRef.current === key) {
        responsibleInjectedCategoriesRef.current = [];
      }

      if (search[key]?.length) {
        dispatch(RESET_FILTERS_SEARCH_BY_KEY({ key }));
      }

      dispatch(RESET_FILTERS_VALUE_BY_KEY({ key }));
      dispatch(RESET_FILTERS_CACHE_BY_KEY({ key }));

      if (!isCascadingFilterKey(key)) return;

      cattegories.forEach(category => {
        if (category.value === key) {
          const categoryData = getInitialCategoryData(category.value);
          const cascadingParams = getCascadingFilterParams(nextValues, category.value);

          if (!hasCascadingFilterParams(cascadingParams) && categoryData) {
            dispatch(SET_FILTER_DATA_BY_KEY({ key: category.value, data: categoryData }));
          } else if (!hasCascadingFilterParams(cascadingParams)) {
            dispatch(SET_FILTER_DATA_BY_KEY({ key: category.value, data: [] }));
          }

          lastLoadedCategoryCascadingSignatureRef.current[category.value] = '';
          return;
        }

        lastLoadedCategoryCascadingSignatureRef.current[category.value] = '';
      });
    },
    [cattegories, dispatch, getInitialCategoryData, search, values],
  );

  const onCleanAll = useCallback(() => {
    resetPriceManagerFilters(dispatch);
    clearCompetitorFilters();
    responsibleIdRef.current = null;
    responsibleInjectedCategoriesRef.current = [];
    responsibleCategoryLevelRef.current = null;

    cattegories.forEach(category => {
      const categoryData = getInitialCategoryData(category.value);
      lastLoadedCategoryCascadingSignatureRef.current[category.value] = '';

      if (categoryData) {
        dispatch(SET_FILTER_DATA_BY_KEY({ key: category.value, data: categoryData }));
      }
    });
  }, [cattegories, clearCompetitorFilters, dispatch, getInitialCategoryData]);

  const onOpen = useCallback(
    (key: Keys) => {
      const cascadingParams = getCascadingFilterParams(values, key);
      const cascadingSignature = getCascadingFilterParamsSignature(cascadingParams);
      const categoryIndex = cattegories.findIndex(({ value }) => value === key);
      if (categoryIndex === -1) return;

      if (hasCascadingFilterParams(cascadingParams)) {
        if (!search[key] && lastLoadedCategoryCascadingSignatureRef.current[key] === cascadingSignature) return;
        results[categoryIndex]?.refetch();
        return;
      }

      if (!search[key]) {
        const categoryData = getInitialCategoryData(key);

        if (categoryData) {
          lastLoadedCategoryCascadingSignatureRef.current[key] = cascadingSignature;
          const selectedValues = new Set((values[key] as string[] | undefined) ?? []);
          const selectedData = ((cache[key] as DataItem[] | undefined) ?? []).filter(({ value }) => selectedValues.has(value));
          const visibleData = _.uniqBy([...selectedData, ...categoryData], 'value');

          dispatch(SET_FILTER_DATA_BY_KEY({ key, data: visibleData }));
        }
      }
    },
    [cache, cattegories, dispatch, getInitialCategoryData, results, search, values],
  );

  const loadingCattegories = useMemo(() => results.some(({ fetchStatus }) => checkIfIsLoading(fetchStatus)), [results]);

  const loading = useMemo(
    () => ({
      productFamilyIds: checkIfIsLoading(loadingProductFamilyIds),
      storeIds: checkIfIsLoading(loadingStoreIds),
      priceStatus: checkIfIsLoading(loadingPriceStatus),
      segmentos: checkIfIsLoading(loadingSegmentos),
      productBrand: checkIfIsLoading(loadingProductBrand),
      supplier: checkIfIsLoading(loadingSupplier),
      loadingCattegories,
      clusters: checkIfIsLoading(loadingClusters),
      storeType: checkIfIsLoading(loadingStoreType),
      responsible: checkIfIsLoading(loadingPricingScopeLevel) || checkIfIsLoading(loadingResponsibleAssignments),
    }),
    [
      loadingProductFamilyIds,
      loadingStoreIds,
      loadingPriceStatus,
      loadingSegmentos,
      loadingProductBrand,
      loadingSupplier,
      loadingCattegories,
      loadingClusters,
      loadingStoreType,
      loadingPricingScopeLevel,
      loadingResponsibleAssignments,
    ],
  );

  const onSelectAll = useCallback(
    (key: Keys, checked: boolean) => {
      let newData: string[] = [];

      if (checked) {
        newData = data[key]?.map(({ value }) => value) ?? [];
      }

      dispatch(SET_FILTERS_VALUE_BY_KEY({ key, value: newData ?? [] }));
    },
    [dispatch, data],
  );

  return {
    data,
    values,
    search,
    cattegories,
    loading,
    isVisible,
    onSearch,
    onChange,
    onResponsibleChange,
    onClean,
    onCleanResponsible,
    onCleanAll,
    onOpen,
    onSelectAll,
  };
};

export default useNewFilterBox;
